/**
 * Column Mapping table for the SFTP Push tab. Maps each BQ table column name to
 * its final file column header. Cells are inline-editable (pencil icon) and rows
 * have an inline delete; "Add new column" lives in the table toolbar. Row order
 * is the SFTP file column order.
 */
import { useMemo } from "react";
import CustomTable from "../../../../../components/custom-table/custom-table";
import { TableCellType } from "../../../../../components/custom-table/table";
import { ColumnMappingRow } from "./nycbsSftpPushRows";

const TABLE_NAME = "sftp-column-mapping-table";

const editableTextHeader = (
  key: string,
  label: string,
  order: number,
  width: number,
) => ({
  key,
  label,
  order,
  width,
  sortable: false,
  subKey: "value",
  type: TableCellType.EDITABLE_TEXT_WITH_ICON,
});

const TABLE_HEADERS = [
  {
    key: "bqColumn",
    label: "BQ Table Column Name",
    order: 0,
    width: 46,
    sortable: false,
    type: TableCellType.STRING,
  },
  editableTextHeader("finalHeader", "Final File Column Name", 1, 46),
  {
    key: "delete",
    label: "",
    order: 2,
    width: 8,
    sortable: false,
    type: TableCellType.DELETE_ICON,
  },
];

interface ColumnMappingTableProps {
  resetKey: number;
  rows: ColumnMappingRow[];
  onChange: (rows: ColumnMappingRow[]) => void;
  onAdd: () => void;
}

const ColumnMappingTable = ({
  resetKey,
  rows,
  onChange,
  onAdd,
}: ColumnMappingTableProps) => {
  const tableData = useMemo(
    () =>
      rows.map((row) => ({
        id: row.id,
        bqColumn: row.bqColumn || "—",
        finalHeader: {
          value: row.finalHeader,
          placeholder: "File header",
          emptyLabel: "—",
        },
        delete: row.id,
      })),
    [rows],
  );

  const handleCellChange = (
    rowData: { id?: string },
    fieldKey: string,
    newValue: string,
  ) => {
    if (!rowData?.id) return;
    onChange(
      rows.map((row) =>
        row.id === rowData.id ? { ...row, [fieldKey]: newValue } : row,
      ),
    );
  };

  const handleDelete = (rowData: { id?: string }) => {
    if (rowData?.id) onChange(rows.filter((row) => row.id !== rowData.id));
  };

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-primaryGray-16 p-4">
      <div className="text-sm font-semibold text-primaryGray-1">
        Column Mapping
      </div>
      <CustomTable
        key={`${TABLE_NAME}-${resetKey}`}
        tableName={TABLE_NAME}
        tableHeaders={TABLE_HEADERS}
        tableData={tableData}
        count={tableData.length}
        totalCount={tableData.length}
        itemsPerPage={200}
        isFetching={false}
        hideSearchBar
        hidePagination
        showOtherButton
        otherButtonMeta={[
          {
            text: "Add new column",
            onClick: onAdd,
            disabled: false,
            buttonType: "primary",
            size: "medium",
          },
        ]}
        onCellValueChange={handleCellChange}
        onDeleteIconClick={handleDelete}
      />
    </div>
  );
};

export default ColumnMappingTable;
